import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SubPortfolioSelect } from "@/components/SubPortfolioSelect";
import { usePrivacy } from "@/context/PrivacyContext";
import { fmtCurrency, fmtSigned, fmtDate, gainClass } from "@/lib/format";
import { Download } from "lucide-react";

export default function Tax() {
    const { mask } = usePrivacy();
    const [rsub, setRsub] = useState("ALL");
    const [ryear, setRyear] = useState("ALL");
    const { data } = useQuery({ queryKey: ["realized-report", rsub], queryFn: async () => (await api.get(`/reports/realized?sub=${rsub}`)).data });
    const years = data ? data.by_year.map((y) => y.year) : [];
    const rows = data ? (ryear === "ALL" ? data.by_year : data.by_year.filter((y) => y.year === ryear)) : [];
    const totalRealized = rows.reduce((a, r) => a + r.realized_pl, 0);
    const totalDiv = rows.reduce((a, r) => a + r.dividends, 0);
    const totalFees = rows.reduce((a, r) => a + (r.fees || 0), 0);
    const totalTax = rows.reduce((a, r) => a + (r.taxes || 0), 0);
    const sales = data ? (ryear === "ALL" ? data.sales : data.sales.filter((s) => s.date.slice(0, 4) === ryear)) : [];
    const divs = data ? (ryear === "ALL" ? data.dividends : data.dividends.filter((d) => d.date.slice(0, 4) === ryear)) : [];

    const download = async () => {
        const res = await api.get(`/reports/realized/export?sub=${rsub}&year=${ryear}`, { responseType: "blob" });
        const url = URL.createObjectURL(res.data);
        const a = document.createElement("a"); a.href = url; a.download = `tax_report_${rsub}_${ryear}.csv`; a.click(); URL.revokeObjectURL(url);
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="font-heading text-2xl font-bold tracking-tight">Tax Reports</h1>
                    <p className="text-sm text-muted-foreground">Realized gains & dividend income for tax reporting</p>
                </div>
                <div className="flex items-center gap-2">
                    <SubPortfolioSelect value={rsub} onChange={setRsub} testId="tax-sub-select" />
                    <Select value={ryear} onValueChange={setRyear}>
                        <SelectTrigger className="w-[120px] num" data-testid="tax-year-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="ALL">All Years</SelectItem>
                            {years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Button variant="outline" onClick={download} data-testid="download-tax-report-btn"><Download className="w-4 h-4 mr-1.5" />Download CSV</Button>
                </div>
            </div>

            <Card className="p-5">
                <h2 className="font-heading text-lg font-semibold mb-4">Summary by Year</h2>
                {!data ? <div className="text-sm text-muted-foreground">Loading…</div> : rows.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No realized gains or dividends for this selection.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="tax-report-table">
                            <thead className="border-b border-border text-xs uppercase tracking-wider text-muted-foreground">
                                <tr><th className="text-left py-2">Year</th><th className="text-right py-2">Realized P/L</th><th className="text-right py-2">Dividends</th><th className="text-right py-2">Fees</th><th className="text-right py-2">Tax</th><th className="text-right py-2">Total</th></tr>
                            </thead>
                            <tbody>
                                {rows.map((y) => (
                                    <tr key={y.year} className="border-b border-border/50" data-testid={`tax-year-${y.year}`}>
                                        <td className="py-2 num font-medium">{y.year}</td>
                                        <td className={`py-2 text-right num ${gainClass(y.realized_pl)}`}>{mask(fmtSigned(y.realized_pl))}</td>
                                        <td className="py-2 text-right num">{mask(fmtCurrency(y.dividends))}</td>
                                        <td className="py-2 text-right num text-muted-foreground">{mask(fmtCurrency(y.fees || 0))}</td>
                                        <td className="py-2 text-right num text-muted-foreground">{mask(fmtCurrency(y.taxes || 0))}</td>
                                        <td className={`py-2 text-right num font-medium ${gainClass(y.total)}`}>{mask(fmtSigned(y.total))}</td>
                                    </tr>
                                ))}
                                <tr className="font-semibold">
                                    <td className="py-2">{ryear === "ALL" ? "All-time" : ryear}</td>
                                    <td className={`py-2 text-right num ${gainClass(totalRealized)}`}>{mask(fmtSigned(totalRealized))}</td>
                                    <td className="py-2 text-right num">{mask(fmtCurrency(totalDiv))}</td>
                                    <td className="py-2 text-right num text-muted-foreground">{mask(fmtCurrency(totalFees))}</td>
                                    <td className="py-2 text-right num text-muted-foreground">{mask(fmtCurrency(totalTax))}</td>
                                    <td className="py-2 text-right num">{mask(fmtSigned(totalRealized + totalDiv))}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            <div className="grid grid-cols-2 gap-4">
                <Card className="p-5" data-testid="total-fees-card">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">Total Fees Paid</div>
                    <div className="num text-2xl font-bold mt-1">{data ? mask(fmtCurrency(totalFees)) : "—"}</div>
                    <div className="text-[11px] text-muted-foreground mt-1">{ryear === "ALL" ? "All-time" : ryear}, from buys &amp; sells</div>
                </Card>
                <Card className="p-5" data-testid="total-tax-card">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">Total Tax Withheld</div>
                    <div className="num text-2xl font-bold mt-1">{data ? mask(fmtCurrency(totalTax)) : "—"}</div>
                    <div className="text-[11px] text-muted-foreground mt-1">{ryear === "ALL" ? "All-time" : ryear}, dividend withholding</div>
                </Card>
            </div>

            <Card className="p-5">
                <h2 className="font-heading text-lg font-semibold mb-4">Dividend Income</h2>
                {divs.length === 0 ? <p className="text-sm text-muted-foreground">No dividends in this selection.</p> : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="dividend-table">
                            <thead className="border-b border-border text-xs uppercase tracking-wider text-muted-foreground">
                                <tr><th className="text-left py-2">Date</th><th className="text-left py-2">Ticker</th><th className="text-left py-2">Sub</th><th className="text-right py-2">Gross</th><th className="text-right py-2">Tax Withheld</th><th className="text-right py-2">% Tax</th><th className="text-right py-2">Net</th></tr>
                            </thead>
                            <tbody>
                                {divs.map((d, i) => (
                                    <tr key={`${d.date}-${d.ticker}-${i}`} className="border-b border-border/50" data-testid={`dividend-row-${i}`}>
                                        <td className="py-2 num">{fmtDate(d.date)}</td>
                                        <td className="py-2 num font-medium">{d.ticker || "—"}</td>
                                        <td className="py-2 text-muted-foreground">{d.sub_portfolio}</td>
                                        <td className="py-2 text-right num">{mask(fmtCurrency(d.gross))}</td>
                                        <td className="py-2 text-right num text-muted-foreground">{mask(fmtCurrency(d.tax_withheld))}</td>
                                        <td className="py-2 text-right num text-muted-foreground">{(d.tax_pct ?? 0).toFixed(1)}%</td>
                                        <td className="py-2 text-right num font-medium">{mask(fmtCurrency(d.amount))}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            <Card className="p-5">
                <h2 className="font-heading text-lg font-semibold mb-4">Realized Gains (per sale)</h2>
                {sales.length === 0 ? <p className="text-sm text-muted-foreground">No sales in this selection.</p> : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="border-b border-border text-xs uppercase tracking-wider text-muted-foreground">
                                <tr><th className="text-left py-2">Date</th><th className="text-left py-2">Ticker</th><th className="text-left py-2">Sub</th><th className="text-right py-2">Qty</th><th className="text-right py-2">Proceeds</th><th className="text-right py-2">Cost Basis</th><th className="text-right py-2">Realized P/L</th></tr>
                            </thead>
                            <tbody>
                                {sales.map((sl, i) => (
                                    <tr key={`${sl.date}-${sl.ticker}-${i}`} className="border-b border-border/50">
                                        <td className="py-2 num">{fmtDate(sl.date)}</td>
                                        <td className="py-2 num font-medium">{sl.ticker}</td>
                                        <td className="py-2 text-muted-foreground">{sl.sub_portfolio}</td>
                                        <td className="py-2 text-right num">{sl.quantity}</td>
                                        <td className="py-2 text-right num">{mask(fmtCurrency(sl.proceeds))}</td>
                                        <td className="py-2 text-right num">{mask(fmtCurrency(sl.cost_basis))}</td>
                                        <td className={`py-2 text-right num ${gainClass(sl.realized_pl)}`}>{mask(fmtSigned(sl.realized_pl))}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                <p className="text-[11px] text-muted-foreground mt-3">Average-cost method (fees included). This is not tax advice — confirm figures with your tax professional.</p>
            </Card>
        </div>
    );
}
